import React, { useEffect } from "react"; 
import { Link } from "react-router-dom"; 
import { Calendar, User, ArrowRight } from "lucide-react";
import "aos/dist/aos.css";
import { Helmet } from "react-helmet-async"; 
import blog from '../Img/blog.jpeg'

const BlogList = () => {
  useEffect(() => {
    import("aos").then((AOS) => {
      AOS.default.init({ duration: 500, once: true });
    });
  }, []);

  const blogs = [
    {
      id: 1,
      title: "Top 5 Benefits of Playing Browser Games",
      excerpt: "Browser games are not just for fun. Discover how playing HTML5 games can improve your reflexes, strategic thinking, and stress relief without any downloads.",
      date: "Jan 28, 2026",
      author: "Game Zone Editor", 
      image: "https://images.unsplash.com/photo-1552820728-8b83bb6b773f?q=80&w=900&auto=format&fit=crop",
      slug: "benefits-of-browser-games"
    },
    {
      id: 2,
      title: "Why HTML5 Games are the Future of Gaming",
      excerpt: "Gone are the days of heavy installations. Learn why HTML5 technology is revolutionizing the gaming industry and allowing you to play free online instantly.",
      date: "Jan 25, 2026",
      author: "Tech Desk",
      image: "https://images.unsplash.com/photo-1550745165-9bc0b252726f?q=80&w=900&auto=format&fit=crop",
      slug: "html5-games-future"
    },
    {
      id: 3,
      title: "Best Racing Games to Play Online in 2026",
      excerpt: "Looking for speed? We have curated a list of the most thrilling and high-speed racing games available to play free online on Game Zone Hub.",
      date: "Jan 20, 2026",
      author: "Game Zone Team",
      image: "https://images.unsplash.com/photo-1511512578047-dfb367046420?q=80&w=900&auto=format&fit=crop",
      slug: "best-racing-games-2026"
    },
    // 🚀 NEW BLOG 1 ADDED
    {
      id: 4,
      title: "How to Optimize Your Browser for Lag-Free Gaming",
      excerpt: "Experiencing lag while playing online games? Follow our ultimate guide to tweak Chrome, Edge, and Firefox settings for maximum FPS and smooth gameplay.",
      date: "Feb 05, 2026",
      author: "Tech Support",
      image: "https://images.unsplash.com/photo-1542751371-adc38448a05e?q=80&w=900&auto=format&fit=crop",
      slug: "optimize-browser-for-gaming"
    },
    // 🚀 NEW BLOG 2 ADDED
    {
      id: 5,
      title: "The Evolution of Multiplayer IO Games",
      excerpt: "From Agar.io to complex 3D battle royales, explore how .IO games transformed the landscape of massively multiplayer browser gaming forever.",
      date: "Feb 12, 2026",
      author: "Gaming Historian",
      image: `${blog}`,
      slug: "evolution-of-io-games"
    }
  ];

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "Game Zone Hub Gaming Blog",
    "description": "Latest news, tips, and updates about free online HTML5 browser games.",
    "url": "https://www.gamezonehub.in/",
    "blogPost": blogs.map(blog => ({
      "@type": "BlogPosting",
      "headline": blog.title,
      "image": blog.image,
      "datePublished": new Date(blog.date).toISOString(),
      "author": {
        "@type": "Organization",
        "name": blog.author
      },
      "url": `https://www.gamezonehub.in/blog/${blog.slug}`
    }))
  };

  return (
    <div id="blog-section" className="bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] sm:pt-[70px] pt-[50px] sm:pb-20 pb-10">
      
      <Helmet>
        {schemaData && (
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />
        )}
      </Helmet>

      <div className="container mx-auto px-4">
        <div className="text-center mb-12" data-aos="fade-down">
          <h2 className="text-3xl sm:text-5xl font-extrabold text-white mb-4">
            Gaming <span className="text-blue-500">Blog</span> & News
          </h2>
          <p className="text-gray-400 max-w-3xl mx-auto text-lg leading-relaxed">
            Welcome to the official <strong>Game Zone Hub Blog</strong>. Stay updated with the latest gaming trends, expert tips, comprehensive reviews, and ultimate guides for your favorite <strong>free online HTML5 games</strong>.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {blogs.map((blog, index) => (
            <article 
              key={blog.id} 
              className="bg-[#1e293b] rounded-2xl overflow-hidden border border-white/5 hover:border-blue-500/30 transition-all duration-300 group hover:-translate-y-2 shadow-lg flex flex-col"
              data-aos="fade-up"
              data-aos-delay={index * 100}
            >
              <div className="relative h-48 overflow-hidden shrink-0">
                <img 
                  src={blog.image} 
                  alt={`${blog.title} - Game Zone Hub Browser Games Blog`}
                  title={blog.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  loading="lazy"
                />
                <div className="absolute top-4 left-4 bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-md">
                  Gaming News
                </div>
              </div>
              
              <div className="p-6 flex flex-col flex-grow">
                <div className="flex items-center gap-4 text-xs text-gray-400 mb-3">
                  <span className="flex items-center gap-1"><Calendar size={12}/> {blog.date}</span>
                  <span className="flex items-center gap-1"><User size={12}/> {blog.author}</span>
                </div>
                
                <h3 className="text-xl font-bold text-white mb-3 line-clamp-2 group-hover:text-blue-400 transition-colors">
                  {blog.title}
                </h3>
                
                <p className="text-gray-400 text-sm mb-6 line-clamp-3 flex-grow">
                  {blog.excerpt}
                </p>
                
                <Link
                  to={`/blog/${blog.slug}`} 
                  className="inline-flex items-center gap-2 text-blue-400 font-semibold hover:text-blue-300 transition-colors text-sm cursor-pointer mt-auto w-fit"
                  aria-label={`Read full article about: ${blog.title}`}
                >
                  Read Full Article <ArrowRight size={16}/>
                </Link>
              </div>
            </article>
          ))}
        </div>

        <div className="sm:mt-16 mt-10 bg-black/30 p-8 rounded-2xl border border-white/5 text-center" data-aos="fade-up">
            <h3 className="text-2xl font-bold text-white mb-3">Level Up Your Gaming Knowledge</h3>
            <p className="text-gray-400 text-sm sm:text-base max-w-4xl mx-auto leading-relaxed">
               Our editorial team is dedicated to bringing you the best insights from the world of browser-based entertainment. Whether you want to discover the top-rated multiplayer games, learn strategies to beat hard levels, or understand the technology behind <strong>playing free online games without downloads</strong>, our blog is your ultimate resource.
            </p>
        </div>

      </div>
    </div>
  );
};

export default BlogList;